import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { createTeam, teamQueries } from '@/api/endpoints/teams'
import { apiErrorMessage } from '@/api/errors'
import { Form } from '@/components/kit/form-fields'
import { Button } from '@/components/ui/button'
import { TeamDetailsFields } from '@/features/teams/team-details-fields'
import { teamFormSchema, type TeamFormValues } from '@/features/teams/team-form-schema'
import { toTeamCreateInput } from '@/features/teams/to-team-write'
import { toDateString } from '@/lib/dates'

export const Route = createFileRoute('/_authed/teams/new')({
  component: NewTeamPage,
})

const teamsRoute = getRouteApi('/_authed/teams')

function NewTeamPage() {
  const listSearch = teamsRoute.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const form = useForm<TeamFormValues>({
    resolver: zodResolver(teamFormSchema),
    defaultValues: {
      name: '',
      tag: '',
      server: '',
      creationDate: toDateString(new Date()),
    },
  })

  const create = useMutation({
    mutationFn: (values: TeamFormValues) => createTeam(toTeamCreateInput(values)),
    onSuccess: async (team) => {
      void queryClient.invalidateQueries({ queryKey: teamQueries.lists })
      toast.success('Team created')
      if (team.uuid) {
        queryClient.setQueryData(teamQueries.detail(team.uuid).queryKey, team)
        await navigate({ to: '/teams/$uuid', params: { uuid: team.uuid }, search: listSearch })
        return
      }
      // Leave the form without a uuid to open, so a retry cannot create the team a second time.
      await navigate({ to: '/teams', search: listSearch })
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  })

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/teams" search={listSearch}>
            <ArrowLeft />
            Teams
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">New team</h1>
      </div>

      <Form form={form} onSubmit={(values) => create.mutate(values)} className="grid gap-4">
        <TeamDetailsFields />
        <div className="flex flex-wrap gap-2 pt-2">
          <Button type="submit" disabled={create.isPending}>
            Create team
          </Button>
        </div>
      </Form>
    </div>
  )
}
